import AdminLayout from "../../layouts/AdminLayout";
import useAuthStore from "../../store/authStore";
import ManagerLayout from "../../layouts/ManagerLayout";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import apiClient from "../../api/apiClient";

// ─── CreateLocationModal ───────────────────────────────────────────────────────
function CreateLocationModal({ onClose, onCreated }) {
  const EMPTY_FORM = {
    code: "", name: "", timezone: "Asia/Kolkata",
    latitude: "", longitude: "", allowed_radius_meters: 100, allowed_ips: "",
  };
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [errors,  setErrors]  = useState({});
  const [step,    setStep]    = useState(1);

  const TIMEZONES = [
    { value: "Asia/Kolkata",         label: "Asia/Kolkata           (UTC +5:30)"   },
    { value: "Asia/Dubai",           label: "Asia/Dubai             (UTC +4)"      },
    { value: "Asia/Karachi",         label: "Asia/Karachi           (UTC +5)"      },
    { value: "Asia/Dhaka",           label: "Asia/Dhaka             (UTC +6)"      },
    { value: "Asia/Colombo",         label: "Asia/Colombo           (UTC +5:30)"   },
    { value: "Asia/Kathmandu",       label: "Asia/Kathmandu         (UTC +5:45)"   },
    { value: "Asia/Singapore",       label: "Asia/Singapore         (UTC +8)"      },
    { value: "Asia/Tokyo",           label: "Asia/Tokyo             (UTC +9)"      },
    { value: "Asia/Seoul",           label: "Asia/Seoul             (UTC +9)"      },
    { value: "Asia/Shanghai",        label: "Asia/Shanghai          (UTC +8)"      },
    { value: "Asia/Bangkok",         label: "Asia/Bangkok           (UTC +7)"      },
    { value: "Asia/Jakarta",         label: "Asia/Jakarta           (UTC +7)"      },
    { value: "Asia/Riyadh",          label: "Asia/Riyadh            (UTC +3)"      },
    { value: "Asia/Kuwait",          label: "Asia/Kuwait            (UTC +3)"      },
    { value: "Asia/Tehran",          label: "Asia/Tehran            (UTC +3:30)"   },
    { value: "Asia/Baghdad",         label: "Asia/Baghdad           (UTC +3)"      },
    { value: "Asia/Jerusalem",       label: "Asia/Jerusalem         (UTC +2/+3)"   },
    { value: "Asia/Tashkent",        label: "Asia/Tashkent          (UTC +5)"      },
    { value: "Australia/Sydney",     label: "Australia/Sydney       (UTC +10/+11)" },
    { value: "Australia/Perth",      label: "Australia/Perth        (UTC +8)"      },
    { value: "Australia/Brisbane",   label: "Australia/Brisbane     (UTC +10)"     },
    { value: "Pacific/Auckland",     label: "Pacific/Auckland       (UTC +12/+13)" },
    { value: "Pacific/Honolulu",     label: "Pacific/Honolulu       (UTC −10)"     },
    { value: "Europe/London",        label: "Europe/London          (UTC 0/+1)"    },
    { value: "Europe/Paris",         label: "Europe/Paris           (UTC +1/+2)"   },
    { value: "Europe/Berlin",        label: "Europe/Berlin          (UTC +1/+2)"   },
    { value: "Europe/Moscow",        label: "Europe/Moscow          (UTC +3)"      },
    { value: "Europe/Istanbul",      label: "Europe/Istanbul        (UTC +3)"      },
    { value: "Africa/Cairo",         label: "Africa/Cairo           (UTC +2)"      },
    { value: "Africa/Lagos",         label: "Africa/Lagos           (UTC +1)"      },
    { value: "Africa/Nairobi",       label: "Africa/Nairobi         (UTC +3)"      },
    { value: "Africa/Johannesburg",  label: "Africa/Johannesburg    (UTC +2)"      },
    { value: "America/New_York",     label: "America/New_York       (UTC −5/−4)"   },
    { value: "America/Chicago",      label: "America/Chicago        (UTC −6/−5)"   },
    { value: "America/Denver",       label: "America/Denver         (UTC −7/−6)"   },
    { value: "America/Los_Angeles",  label: "America/Los_Angeles    (UTC −8/−7)"   },
    { value: "America/Toronto",      label: "America/Toronto        (UTC −5/−4)"   },
    { value: "America/Sao_Paulo",    label: "America/Sao_Paulo      (UTC −3/−2)"   },
    { value: "America/Buenos_Aires", label: "America/Buenos_Aires   (UTC −3)"      },
    { value: "America/Mexico_City",  label: "America/Mexico_City    (UTC −6/−5)"   },
    { value: "UTC",                  label: "UTC                    (UTC 0)"       },
  ];

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: name === "code" ? value.toUpperCase() : value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  }

  function validateStep1() {
    const errs = {};
    if (!form.code.trim()) errs.code = "Location code is required.";
    if (!form.name.trim()) errs.name = "Location name is required.";
    return errs;
  }

  function validateAll() {
    const errs = validateStep1();
    const lat = form.latitude  !== "" ? parseFloat(form.latitude)  : null;
    const lng = form.longitude !== "" ? parseFloat(form.longitude) : null;
    if (form.latitude  !== "" && (isNaN(lat) || lat < -90  || lat > 90))  errs.latitude  = "Must be between −90 and 90.";
    if (form.longitude !== "" && (isNaN(lng) || lng < -180 || lng > 180)) errs.longitude = "Must be between −180 and 180.";
    if ((form.latitude === "") !== (form.longitude === "")) {
      errs.latitude  = "Provide both lat & lng, or neither.";
      errs.longitude = "Provide both lat & lng, or neither.";
    }
    if (form.allowed_radius_meters !== "" && parseInt(form.allowed_radius_meters) < 1)
      errs.allowed_radius_meters = "Radius must be at least 1 metre.";
    return errs;
  }

  function handleNextStep(e) {
    e.preventDefault();
    e.stopPropagation();
    const errs = validateStep1();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setStep(2);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    const errs = validateAll();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    try {
      setLoading(true);
      const payload = {
        code:                  form.code.trim(),
        name:                  form.name.trim(),
        timezone:              form.timezone,
        allowed_ips:           form.allowed_ips,
        allowed_radius_meters: form.allowed_radius_meters || 100,
        ...(form.latitude  !== "" && { latitude:  parseFloat(form.latitude)  }),
        ...(form.longitude !== "" && { longitude: parseFloat(form.longitude) }),
      };
      const res = await apiClient.post("/accounts/api/locations/", payload);
      onCreated(res.data);
      onClose();
    } catch (err) {
      setErrors({
        api: err.response?.data?.error || err.response?.data?.detail || "Failed to create location.",
      });
    } finally {
      setLoading(false);
    }
  }

  function handleDetectGPS() {
    if (!navigator.geolocation) {
      setErrors((p) => ({ ...p, latitude: "Geolocation not supported." }));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((prev) => ({
          ...prev,
          latitude:  pos.coords.latitude.toFixed(6),
          longitude: pos.coords.longitude.toFixed(6),
        }));
        setErrors((p) => ({ ...p, latitude: "", longitude: "" }));
      },
      () => setErrors((p) => ({ ...p, latitude: "Could not detect location. Check browser permissions." }))
    );
  }

  const inputCls    = "w-full bg-white/10 border border-white/30 hover:border-white/50 focus:border-violet-400 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-white text-sm outline-none transition-colors placeholder:text-white/30";
  const inputErrCls = "w-full bg-white/10 border border-red-400/60 focus:border-red-400 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-white text-sm outline-none transition-colors placeholder:text-white/30";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.88, opacity: 0, y: 24 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.88, opacity: 0, y: 24 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
        className="bg-gray-900/98 border border-white/15 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-7 pt-5 sm:pt-7 pb-4 sm:pb-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center shadow-lg text-lg sm:text-xl">
              📍
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white">Add New Location</h2>
              <p className="text-white/45 text-xs">Configure office GPS + IP restrictions</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-all text-sm"
          >
            ✕
          </button>
        </div>

        {/* Step Tabs */}
        <div className="flex border-b border-white/10">
          {["Basic Info", "Restrictions"].map((label, i) => (
            <button
              key={label}
              type="button"
              onClick={() => {
                if (i + 1 === 2) {
                  const errs = validateStep1();
                  if (Object.keys(errs).length) { setErrors(errs); return; }
                }
                setErrors({});
                setStep(i + 1);
              }}
              className={`flex-1 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold transition-all ${
                step === i + 1
                  ? "text-violet-400 border-b-2 border-violet-400 bg-violet-500/10"
                  : "text-white/40 hover:text-white/70"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <div className="px-5 sm:px-7 py-5 sm:py-6 space-y-4 sm:space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-white/75 font-semibold text-xs uppercase tracking-wide">
                  Location Code <span className="text-red-400">*</span>
                </label>
                <input name="code" value={form.code} onChange={handleChange} placeholder="e.g. HYD" maxLength={10}
                  className={errors.code ? inputErrCls : inputCls} />
                {errors.code
                  ? <p className="text-red-400 text-xs">{errors.code}</p>
                  : <p className="text-white/35 text-xs">Short uppercase code</p>}
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-white/75 font-semibold text-xs uppercase tracking-wide">
                  Location Name <span className="text-red-400">*</span>
                </label>
                <input name="name" value={form.name} onChange={handleChange} placeholder="e.g. Hyderabad"
                  className={errors.name ? inputErrCls : inputCls} />
                {errors.name && <p className="text-red-400 text-xs">{errors.name}</p>}
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-white/75 font-semibold text-xs uppercase tracking-wide">Timezone</label>
              <select name="timezone" value={form.timezone} onChange={handleChange}
                className={`${inputCls} font-mono`} style={{ backgroundImage: "none" }}>
                {TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value} className="bg-gray-900 font-mono">{tz.label}</option>
                ))}
              </select>
              <p className="text-white/35 text-xs">
                Selected: <span className="text-violet-300 font-semibold">{form.timezone}</span>
              </p>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose}
                className="flex-1 px-4 py-2.5 sm:py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-sm transition-all">
                Cancel
              </button>
              <button type="button" onClick={handleNextStep}
                className="flex-1 px-4 py-2.5 sm:py-3 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-bold text-sm shadow-lg transition-all">
                Next →
              </button>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <form onSubmit={handleSubmit} className="px-5 sm:px-7 py-5 sm:py-6 space-y-4 sm:space-y-5">
            <div className="rounded-xl border border-white/10 bg-white/5 p-3 sm:p-4 space-y-3 sm:space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-green-400 text-base">🌍</span>
                  <span className="text-white font-semibold text-sm">GPS Geofence</span>
                </div>
                <button type="button" onClick={handleDetectGPS}
                  className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg bg-green-500/15 hover:bg-green-500/28 border border-green-400/30 text-green-400 hover:text-green-300 text-xs font-semibold transition-all">
                  📍 Auto-detect
                </button>
              </div>
              <p className="text-white/40 text-xs">Leave blank to skip GPS validation.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <label className="text-white/75 font-semibold text-xs uppercase tracking-wide">Latitude</label>
                  <input name="latitude" type="number" step="0.000001" value={form.latitude}
                    onChange={handleChange} placeholder="17.385044"
                    className={errors.latitude ? inputErrCls : inputCls} />
                  {errors.latitude && <p className="text-red-400 text-xs">{errors.latitude}</p>}
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-white/75 font-semibold text-xs uppercase tracking-wide">Longitude</label>
                  <input name="longitude" type="number" step="0.000001" value={form.longitude}
                    onChange={handleChange} placeholder="78.486671"
                    className={errors.longitude ? inputErrCls : inputCls} />
                  {errors.longitude && <p className="text-red-400 text-xs">{errors.longitude}</p>}
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-white/75 font-semibold text-xs uppercase tracking-wide">
                  Allowed Radius (metres)
                </label>
                <input name="allowed_radius_meters" type="number" min="1"
                  value={form.allowed_radius_meters} onChange={handleChange} placeholder="100"
                  className={errors.allowed_radius_meters ? inputErrCls : inputCls} />
                {errors.allowed_radius_meters
                  ? <p className="text-red-400 text-xs">{errors.allowed_radius_meters}</p>
                  : <p className="text-white/35 text-xs">Default: 100 m.</p>}
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/5 p-3 sm:p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-blue-400 text-base">🔒</span>
                <span className="text-white font-semibold text-sm">IP Whitelist</span>
              </div>
              <p className="text-white/40 text-xs">Leave blank to allow all IPs. Separate multiple with commas.</p>
              <div className="flex flex-col gap-1.5">
                <label className="text-white/75 font-semibold text-xs uppercase tracking-wide">
                  Allowed IP Addresses
                </label>
                <textarea name="allowed_ips" value={form.allowed_ips} onChange={handleChange}
                  placeholder="192.168.1.10, 10.0.0.5" rows={2}
                  className="w-full bg-white/10 border border-white/30 hover:border-white/50 focus:border-violet-400 rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors resize-none placeholder:text-white/30" />
              </div>
            </div>

            {errors.api && (
              <div className="flex items-center gap-2 bg-red-500/15 border border-red-400/30 rounded-xl px-4 py-3">
                <span className="text-red-400 text-sm">⚠ {errors.api}</span>
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setStep(1)}
                className="flex-1 px-4 py-2.5 sm:py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-sm transition-all">
                ← Back
              </button>
              <button type="submit" disabled={loading}
                className="flex-1 px-4 py-2.5 sm:py-3 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-bold text-sm shadow-lg transition-all disabled:opacity-55">
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                    Creating…
                  </span>
                ) : "Create Location"}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}

function LocationSelectWithAdd({ value, onChange, locations, onAddClick, onUpdateClick, isAdmin, isSuperAdmin }) {
  const selected = locations.find((l) => l.code === value);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <label className="text-white font-semibold text-sm">Office Location</label>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              type="button"
              onClick={onAddClick}
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1 rounded-lg bg-violet-500/20 hover:bg-violet-500/35 border border-violet-400/30 hover:border-violet-400/55 text-violet-300 hover:text-white text-xs font-semibold transition-all"
            >
              <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4"/>
              </svg>
              Add Location
            </button>
          )}
          {isSuperAdmin && (
            <button
              type="button"
              onClick={onUpdateClick}
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/35 border border-cyan-400/30 hover:border-cyan-400/55 text-cyan-300 hover:text-white text-xs font-semibold transition-all"
            >
              Update Locations
            </button>
          )}
        </div>
      </div>
      <select
        name="location"
        value={value}
        onChange={onChange}
        className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-violet-400 transition"
      >
        <option value="" className="bg-gray-900">-- Select Location --</option>
        {locations.map((l) => (
          <option key={l.code} value={l.code} className="bg-gray-900">{l.code} – {l.name}</option>
        ))}
      </select>
      {selected && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 sm:gap-3 bg-white/6 border border-white/10 rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 mt-1 flex-wrap"
        >
          <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
            selected.latitude
              ? "bg-green-500/20 text-green-400 border border-green-400/30"
              : "bg-white/8 text-white/35 border border-white/12"
          }`}>
            🌍 {selected.latitude ? `GPS (${selected.allowed_radius_meters} m)` : "No GPS"}
          </span>
          <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
            selected.allowed_ips
              ? "bg-blue-500/20 text-blue-400 border border-blue-400/30"
              : "bg-white/8 text-white/35 border border-white/12"
          }`}>
            🔒 {selected.allowed_ips ? "IP Restricted" : "All IPs OK"}
          </span>
        </motion.div>
      )}
    </div>
  );
}

// ─── Main CreateEmployee ──────────────────────────────────────────────────────
function CreateEmployee() {
  const navigate   = useNavigate();
  const role = useAuthStore((state) => state.role);
  const loggedInId = useAuthStore((state) => state.employeeId);
  const isAdmin    = role === "ADMIN";
  const isManager  = role === "MANAGER";
  const Layout     = isAdmin ? AdminLayout : isManager ? ManagerLayout : null;

  const handleBack = () => {
    if (role === "ADMIN") navigate("/admin/dashboard");
    else if (role === "MANAGER") navigate("/manager/dashboard");
    else navigate("/");
  };

  const [managers,          setManagers]         = useState([]);
  const [locations,         setLocations]         = useState([]);
  const [loadingManagers,   setLoadingManagers]   = useState(true);
  const [loading,           setLoading]           = useState(false);
  const [bulkLoading,       setBulkLoading]       = useState(false);
  const [bulkFile,          setBulkFile]          = useState(null);
  const [loggedManagerName, setLoggedManagerName] = useState("");
  const [showLocationModal, setShowLocationModal] = useState(false);

  // ── SuperAdmin flag ──────────────────────────────────────────────────────────
  // Fetched from the API using the logged-in user's JWT — never from localStorage.
  // localStorage can be tampered with by anyone; the API response is gated behind
  // a valid JWT and the backend's own is_superuser check.
  // This flag only controls the UI (showing/hiding the "Admin" role option).
  // The backend enforces the actual permission independently — so even if someone
  // forced isSuperAdmin=true client-side, the POST would still get a 403.
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const initialFormState = {
    employee_id: "", full_name: "", email: "", designation: "", department: "",
    manager: "", role: isManager ? "EMPLOYEE" : "",
    date_of_birth: "", gender: "MALE", mobile_number: "",
    date_joined: new Date().toLocaleDateString("en-CA"),
    photo: null, location: "",
  };
  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    // ── All init fetches fired in parallel for maximum speed ─────────────────
    const init = async () => {
      const tasks = [];

      // 1. SuperAdmin flag (admin only) + 2. Manager list (admin only) + 3. Manager profile (manager only)
      if (isAdmin && loggedInId) {
        tasks.push(
          // superAdmin flag
          apiClient.get(`/accounts/api/users/${loggedInId}/`)
            .then((res) => setIsSuperAdmin(res.data.user?.is_superuser === true))
            .catch(() => setIsSuperAdmin(false)),
          // managers list
          apiClient.get("/accounts/api/users/")
            .then((res) => setManagers(res.data.users.filter((u) => u.role === "MANAGER")))
            .catch((err) => console.error("Failed to load managers:", err))
            .finally(() => setLoadingManagers(false)),
        );
      } else {
        setLoadingManagers(false);
      }

      if (isManager && loggedInId) {
        tasks.push(
          apiClient.get(`/accounts/api/users/${loggedInId}/`)
            .then((res) => setLoggedManagerName(res.data.user.full_name))
            .catch((err) => console.error("Failed to load manager profile:", err)),
        );
      }

      // locations always needed
      tasks.push(
        apiClient.get("/accounts/api/locations/")
          .then((res) => setLocations(res.data.locations || []))
          .catch((err) => console.error("Failed to load locations:", err)),
      );

      await Promise.all(tasks);
    };
    init();
  }, [isAdmin, isManager, loggedInId]);

  async function handleLocationCreated(data) {
    try {
      const res = await apiClient.get("/accounts/api/locations/");
      setLocations(res.data.locations || []);
      setFormData((prev) => ({ ...prev, location: data.code }));
    } catch {
      /* silent — list refreshes on next open */
    }
  }

  function handleChange(e) {
    const { name, value, files } = e.target;
    if (name === "photo") setFormData((prev) => ({ ...prev, photo: files[0] }));
    else setFormData((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (bulkFile) { alert("Bulk upload selected. Clear bulk file to use manual form."); return; }
    try {
      setLoading(true);
      const payload = new FormData();
      Object.entries(formData).forEach(([key, val]) => {
        if (val !== null && val !== "") payload.append(key, val);
      });
      payload.set("role",    isManager ? "EMPLOYEE" : formData.role);
      payload.set("manager", isManager ? loggedInId : formData.manager || "");
      await apiClient.post("/accounts/api/users/", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Employee created successfully ✅");
      setFormData(initialFormState);
    } catch (err) {
      if (err.response?.data) {
        const message = Object.entries(err.response.data)
          .map(([f, msgs]) => `${f}: ${Array.isArray(msgs) ? msgs.join(", ") : msgs}`)
          .join("\n");
        alert(message);
      } else {
        alert("Failed to create employee");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleBulkUpload(e) {
    e.preventDefault();
    if (!isAdmin)  { alert("Only Admin can perform bulk upload"); return; }
    if (!bulkFile) { alert("Please select a CSV or Excel file");  return; }
    try {
      setBulkLoading(true);
      const payload = new FormData();
      payload.append("excel_file", bulkFile);
      await apiClient.post("/accounts/api/users/", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Bulk employees created successfully ✅");
      setBulkFile(null);
    // AFTER (shows actual server error)
} catch (err) {
  console.error(err);
  const detail =
    err.response?.data?.details ||
    err.response?.data?.error ||
    err.response?.data?.detail ||
    "Bulk upload failed. Check the console for details.";
  alert(`Bulk upload failed:\n\n${detail}`);
} finally {
      setBulkLoading(false);
    }
  }

  // ── Role options ─────────────────────────────────────────────────────────────
  // "Admin" is only shown when isSuperAdmin=true (verified from the API above).
  // Regular admins only see Manager + Employee — matching the backend restriction.
  // Even if someone manually sets isSuperAdmin=true in React DevTools, the backend
  // still returns 403 for role=ADMIN when is_super_admin(request.user) is False.
  const roleOptions = [
    { value: "",         label: "-- Select Role --" },
    ...(isSuperAdmin ? [{ value: "ADMIN", label: "Admin" }] : []),
    { value: "MANAGER",  label: "Manager" },
    { value: "EMPLOYEE", label: "Employee" },
  ];

  return (
    <Layout>
      <AnimatePresence>
        {showLocationModal && (
          <CreateLocationModal
            key="location-modal"
            onClose={() => setShowLocationModal(false)}
            onCreated={handleLocationCreated}
          />
        )}
      </AnimatePresence>

      <div className="p-3 sm:p-6">
        <div className="w-full max-w-[1400px] mx-auto">

          {/* Manual Form */}
          <motion.div
            className={`bg-white/15 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-5 sm:p-8 max-w-4xl mx-auto ${
              bulkFile ? "opacity-50 pointer-events-none" : ""
            }`}
          >
            <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center shadow-lg flex-shrink-0">
                <span className="text-2xl sm:text-3xl">👤</span>
              </div>
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-white">Create New Employee</h1>
                <p className="text-white/60 text-xs sm:text-sm">Add a new team member or upload multiple employees</p>
              </div>
            </div>

            {/* SuperAdmin badge — purely informational, no security value */}
            {isSuperAdmin && (
              <div className="mb-5 sm:mb-6 flex items-center gap-2.5 bg-amber-500/10 border border-amber-400/25 rounded-xl px-4 py-3">
                <span className="text-amber-400 text-base flex-shrink-0">👑</span>
                <p className="text-amber-300 text-xs font-semibold">
                  Super Admin — you can create Admin accounts in addition to Managers and Employees.
                </p>
              </div>
            )}

            <h2 className="text-lg sm:text-xl font-bold text-white mb-5 sm:mb-6">Manual Employee Creation</h2>

            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
              <Row>
                <Input label="Employee ID" name="employee_id" required value={formData.employee_id} onChange={handleChange} />
                <Input label="Full Name"   name="full_name"   required value={formData.full_name}   onChange={handleChange} />
              </Row>
              <Row>
                <Input label="Email ID"    type="email" name="email"        required value={formData.email}       onChange={handleChange} />
                <Input label="Mobile Number" name="mobile_number"  required value={formData.mobile_number} onChange={handleChange} />
              </Row>
              <Row>
                <Input type="date" label="Date of Birth"  name="date_of_birth" required value={formData.date_of_birth} onChange={handleChange} />
                <Select
                  label="Gender"
                  name="gender"
                  required
                  value={formData.gender}
                  onChange={handleChange}
                  options={[
                    { value: "MALE",   label: "Male" },
                    { value: "FEMALE", label: "Female" },
                  ]}
                />
              </Row>
              <Row>
                <Input label="Department" name="department" required value={formData.department} onChange={handleChange} />
                <Input label="Designation" name="designation"               required value={formData.designation} onChange={handleChange} />
              </Row>
              <Row>
                {isAdmin ? (
                  <Select
                    label="Employee Role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    options={roleOptions}
                  />
                ) : (
                  <Input label="Reporting Manager" value={`${loggedInId} - ${loggedManagerName}`} readOnly />
                )}
                {isAdmin ? (
                  <Select
                    label="Reporting Manager"
                    name="manager"
                    value={formData.manager}
                    onChange={handleChange}
                    options={[
                      { value: "", label: loadingManagers ? "Loading…" : "-- Select Manager --" },
                      ...managers.map((m) => ({ value: m.employee_id, label: `${m.employee_id} - ${m.full_name}` })),
                    ]}
                  />
                ) : (
                  <LocationSelectWithAdd
                    value={formData.location}
                    onChange={handleChange}
                    locations={locations}
                    onAddClick={() => setShowLocationModal(true)}
                    onUpdateClick={() => navigate("/admin/update-locations")}
                    isAdmin={isAdmin}
                    isSuperAdmin={isSuperAdmin}
                  />
                )}
              </Row>
              <Row>
                {isAdmin ? (
                  <LocationSelectWithAdd
                    value={formData.location}
                    onChange={handleChange}
                    locations={locations}
                    onAddClick={() => setShowLocationModal(true)}
                    onUpdateClick={() => navigate("/admin/update-locations")}
                    isAdmin={isAdmin}
                    isSuperAdmin={isSuperAdmin}
                  />
                ) : (
                  <Input type="date" label="Date of Joining" name="date_joined" required value={formData.date_joined} onChange={handleChange} />
                )}
                {isAdmin ? (
                  <Input type="date" label="Date of Joining" name="date_joined" required value={formData.date_joined} onChange={handleChange} />
                ) : (
                  <div />
                )}
              </Row>

              <Input label="Profile Photo" type="file" name="photo" required onChange={handleChange} />

              <div className="flex justify-center pt-2 sm:pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-8 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-bold disabled:opacity-60 hover:opacity-90 transition text-sm sm:text-base"
                >
                  {loading ? "Creating…" : "Create Employee"}
                </button>
              </div>
            </form>
          </motion.div>

          {/* Bulk Upload — Admin only */}
          {isAdmin && (
            <motion.div
              className={`bg-white/15 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-5 sm:p-8 max-w-4xl mx-auto mt-8 sm:mt-10 ${
                loading ? "opacity-50 pointer-events-none" : ""
              }`}
            >
              <h2 className="text-lg sm:text-xl font-bold text-white mb-4">Bulk Employee Upload</h2>
              <form onSubmit={handleBulkUpload} className="space-y-5 sm:space-y-6">
                <input
                  type="file"
                  accept=".csv, .xlsx"
                  onChange={(e) => setBulkFile(e.target.files[0])}
                  className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white text-sm"
                />
                <div className="flex justify-center">
                  <button
                    type="submit"
                    disabled={bulkLoading}
                    className="w-full sm:w-auto px-8 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-bold disabled:opacity-60 hover:opacity-90 transition text-sm sm:text-base"
                  >
                    {bulkLoading ? "Uploading…" : "Upload & Create"}
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {/* Back Button */}
          <div className="max-w-4xl mx-auto mt-6 sm:mt-8 flex justify-start">
            <button
              onClick={handleBack}
              className="px-6 py-2.5 sm:px-8 sm:py-3 rounded-xl bg-gray-600 hover:bg-gray-700 text-white font-semibold text-sm sm:text-base shadow transition"
            >
              ← Back to Dashboard
            </button>
          </div>

        </div>
      </div>
    </Layout>
  );
}

// ─── Shared primitives ────────────────────────────────────────────────────────
const Row = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">{children}</div>
);

function Input({ label, ...props }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-white font-semibold text-sm">{label}</label>
      <input
        {...props}
        className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-2.5 sm:py-3 text-white text-sm focus:outline-none focus:border-violet-400 transition"
      />
    </div>
  );
}

function Select({ label, options, ...props }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-white font-semibold text-sm">{label}</label>
      <select
        {...props}
        className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-2.5 sm:py-3 text-white text-sm focus:outline-none focus:border-violet-400 transition"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} className="bg-gray-800">{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

export default CreateEmployee;
